import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import AsyncSelect from "react-select/async";
import { Trash2, AlertTriangle } from "react-feather";
import { useLazyGetProductsQuery } from "../../masters/products/productsApi";
import { useListQuotationPrototypesQuery } from "../../sales/quotations/quotationsApi";
import {
  useLazyGetStockOutCandidatesQuery,
  useLazyGetPrototypeCandidatesQuery,
  useAddCustomerEquipmentMutation,
} from "../amcApi";

const SOURCES = [
  { key: "STOCK_OUT", label: "From a Stock Out", hint: "What was actually issued to this customer" },
  { key: "PROTOTYPE", label: "From a Prototype", hint: "A standard package, then adjust" },
  { key: "PRODUCTS", label: "Pick products", hint: "Straight from the product list" },
];

const WARRANTY_PROVIDERS = [
  { value: "", label: "Not set" },
  { value: "MANUFACTURER", label: "Manufacturer" },
  { value: "SUPPLIER", label: "Supplier" },
  { value: "TEXAS_ALARMS", label: "Texas Alarms" },
];

const emptyRow = () => ({
  key: `${Date.now()}-${Math.random()}`,
  productCode: null,
  productBranchId: null,
  productName: "",
  quantity: 1,
  serialNumber: "",
  installationDate: "",
  warrantyApplicable: true,
  warrantyProvider: "",
  warrantyStartDate: "",
  warrantyMonths: "",
  warrantyNotes: "",
  sourceStockOutLineId: null,
  sourcePrototypeId: null,
  remarks: "",
});

/**
 * Adds equipment to a customer's register from any of the three sources, on one screen -
 * per direct instruction: "select from prototype or from stock out and product list together".
 *
 * Whichever source is picked, every row lands in the same editable grid before saving, so
 * the user can always add or remove rows regardless of where they came from. Only the
 * provenance differs (sourceStockOutLineId / sourcePrototypeId), and the server records it.
 *
 * Skipped rows are always shown with a reason rather than silently dropped - both the
 * prototype prefill (server-supplied ineligibleReason) and the save result (per-row skips).
 */
const AddEquipmentModal = ({ open, onClose, customer, branchId, locationId }) => {
  const [source, setSource] = useState("STOCK_OUT");
  const [rows, setRows] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [prototypeGuid, setPrototypeGuid] = useState("");

  const [fetchProducts] = useLazyGetProductsQuery();
  const [fetchStockOutCandidates, { data: stockOutCandidates = [], isFetching: loadingStockOut }] =
    useLazyGetStockOutCandidatesQuery();
  const [fetchPrototypeCandidates, { isFetching: loadingPrototype }] = useLazyGetPrototypeCandidatesQuery();
  const [addEquipment, { isLoading: isSaving }] = useAddCustomerEquipmentMutation();

  const { data: prototypes = [] } = useListQuotationPrototypesQuery(
    { branchId: branchId ?? undefined, isActive: true },
    { skip: !open || !branchId }
  );

  const customerLabel = customer ? `${customer.custName}` : "";

  // ---- source 1: stock out -------------------------------------------------
  // The dialog opens on the Stock Out source, so load its candidates straight away
  // rather than waiting for a click on the tab that is already selected.
  useEffect(() => {
    if (open && source === "STOCK_OUT" && customer) {
      fetchStockOutCandidates({
        customerBranchId: customer.branchId,
        custId: customer.custId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source, customer?.custId, customer?.branchId]);

  const addStockOutLine = (c) => {
    setRows((prev) => [
      ...prev,
      {
        ...emptyRow(),
        productCode: c.productCode,
        productBranchId: c.productBranchId,
        productName: c.productName,
        quantity: c.availableQty,
        installationDate: c.stockOutDate ? c.stockOutDate.substring(0, 10) : "",
        warrantyApplicable: !!c.hasWarranty,
        warrantyMonths: c.defaultWarrantyMonths ?? "",
        sourceStockOutLineId: c.stockOutLineId,
      },
    ]);
  };

  // ---- source 2: prototype -------------------------------------------------
  const applyPrototype = async (guid) => {
    setPrototypeGuid(guid);
    if (!guid) return;

    const result = await fetchPrototypeCandidates(guid).unwrap().catch(() => null);
    if (!result) {
      toast.error("Could not load that prototype.");
      return;
    }

    const eligible = result.filter((l) => !l.ineligibleReason);
    const ineligible = result.filter((l) => l.ineligibleReason);

    setRows((prev) => [
      ...prev,
      ...eligible.map((l) => ({
        ...emptyRow(),
        productCode: l.productCode,
        productBranchId: l.productBranchId,
        productName: l.productName,
        quantity: l.qty,
        warrantyApplicable: !!l.hasWarranty,
        warrantyMonths: l.defaultWarrantyMonths ?? "",
        sourcePrototypeId: l.prototypeId,
      })),
    ]);

    // Shown, never swallowed - a prototype that quietly loses half its lines is how
    // people stop trusting the screen.
    setSkipped(
      ineligible.map((l) => ({
        productName: l.productName || `Line ${l.lineNumber}`,
        reason: l.ineligibleReason,
      }))
    );

    if (eligible.length === 0) {
      toast.error("No line on that prototype can be tracked as equipment.");
    } else {
      toast.success(`${eligible.length} item(s) added from prototype.`);
    }
  };

  // ---- source 3: plain product list ---------------------------------------
  const loadProductOptions = async (input) => {
    if (!input || input.length < 2) return [];
    const result = await fetchProducts({
      search: input,
      isActive: true,
      branchId: branchId ?? undefined,
      // Only warranty-carrying products can enter the register at all, so the picker
      // never offers anything the server would reject.
      hasWarranty: true,
    })
      .unwrap()
      .catch(() => []);

    return (result || []).map((p) => ({
      value: `${p.branchId}-${p.productCode}`,
      label: `${p.productName} (${p.productCodeNew || p.productCode})`,
      product: p,
    }));
  };

  const addProduct = (opt) => {
    if (!opt) return;
    const p = opt.product;
    setRows((prev) => [
      ...prev,
      {
        ...emptyRow(),
        productCode: p.productCode,
        productBranchId: p.branchId,
        productName: p.productName,
        warrantyApplicable: !!p.hasWarranty,
        warrantyMonths: p.defaultWarrantyMonths ?? "",
      },
    ]);
  };

  // ---- grid ---------------------------------------------------------------
  const setRow = (key, patch) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const removeRow = (key) => setRows((prev) => prev.filter((r) => r.key !== key));

  const sourceTypeForSave = source === "STOCK_OUT" ? "STOCK_OUT" : "BACKLOG";

  const validationError = useMemo(() => {
    if (rows.length === 0) return "Add at least one item.";
    for (const r of rows) {
      if (!r.productCode) return "Every row needs a product.";
      if (!(Number(r.quantity) > 0)) return `Quantity must be more than zero for ${r.productName}.`;
      if (r.serialNumber?.trim() && Number(r.quantity) !== 1)
        return `${r.productName}: a serial number identifies one unit, so quantity must be 1.`;
      if (sourceTypeForSave === "STOCK_OUT" && !r.sourceStockOutLineId)
        return `${r.productName} did not come from a Stock Out line. Add it under "Pick products" instead.`;
    }
    return null;
  }, [rows, sourceTypeForSave]);

  const handleSave = async () => {
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const result = await addEquipment({
        branchId,
        custId: customer.custId,
        customerBranchId: customer.branchId,
        locationId: locationId ?? null,
        sourceType: sourceTypeForSave,
        lines: rows.map((r) => ({
          productCode: r.productCode,
          productBranchId: r.productBranchId,
          quantity: Number(r.quantity),
          serialNumber: r.serialNumber?.trim() || null,
          installationDate: r.installationDate || null,
          warrantyApplicable: !!r.warrantyApplicable,
          warrantyProvider: r.warrantyApplicable ? r.warrantyProvider || null : null,
          warrantyStartDate: r.warrantyApplicable ? r.warrantyStartDate || null : null,
          warrantyMonths: r.warrantyApplicable && r.warrantyMonths !== "" ? Number(r.warrantyMonths) : null,
          warrantyNotes: r.warrantyApplicable ? r.warrantyNotes || null : null,
          sourceStockOutLineId: r.sourceStockOutLineId,
          sourcePrototypeId: r.sourcePrototypeId,
          remarks: r.remarks || null,
        })),
      }).unwrap();

      if (result.skipped?.length) {
        // The save partly succeeded - keep the modal open so the user can see exactly
        // which rows didn't make it and why.
        setSkipped(result.skipped.map((s) => ({ productName: s.productName, reason: s.reason })));
        setRows([]);
        toast.success(`${result.createdCount} added, ${result.skipped.length} skipped.`);
      } else {
        toast.success(`${result.createdCount} item(s) added.`);
        handleClose();
      }
    } catch (err) {
      toast.error(err?.data?.message || "Could not add equipment.");
    }
  };

  const handleClose = () => {
    setRows([]);
    setSkipped([]);
    setPrototypeGuid("");
    setSource("STOCK_OUT");
    onClose();
  };

  if (!open) return null;

  return (
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.45)" }} role="dialog">
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h4 className="mb-0">Add Equipment</h4>
              <small className="text-muted">{customerLabel}</small>
            </div>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            {/* ---- source chooser ---- */}
            <div className="row g-2 mb-3">
              {SOURCES.map((s) => (
                <div className="col-md-4" key={s.key}>
                  <button
                    type="button"
                    className={`btn w-100 text-start p-3 ${
                      source === s.key ? "btn-primary" : "btn-outline-light border"
                    }`}
                    onClick={() => {
                      setSource(s.key);
                      setSkipped([]);
                    }}
                  >
                    <span className="d-block fw-bold">{s.label}</span>
                    <small className={source === s.key ? "text-white-50" : "text-muted"}>{s.hint}</small>
                  </button>
                </div>
              ))}
            </div>

            {/* ---- source 1: stock out ---- */}
            {source === "STOCK_OUT" && (
              <div className="border rounded p-3 mb-3">
                {loadingStockOut && <p className="text-muted mb-0">Loading this customer's Stock Outs…</p>}
                {!loadingStockOut && stockOutCandidates.length === 0 && (
                  <p className="text-muted mb-0">
                    Nothing has been issued to this customer through Stock Out yet. Use a prototype or pick
                    products instead.
                  </p>
                )}
                {!loadingStockOut && stockOutCandidates.length > 0 && (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Stock Out</th>
                          <th>Date</th>
                          <th>Product</th>
                          <th className="text-end">Qty</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {stockOutCandidates.map((c) => (
                          <tr key={c.stockOutLineId}>
                            <td>{c.stockOutNo}</td>
                            <td>{c.stockOutDate?.substring(0, 10)}</td>
                            <td>
                              {c.productName}
                              {!c.hasWarranty && (
                                <small className="d-block text-warning">
                                  Not marked as carrying a warranty — flag it on the product master first
                                </small>
                              )}
                            </td>
                            <td className="text-end">{c.availableQty}</td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                disabled={c.alreadyAdded || !c.hasWarranty}
                                onClick={() => addStockOutLine(c)}
                              >
                                {c.alreadyAdded ? "Already added" : "Add"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ---- source 2: prototype ---- */}
            {source === "PROTOTYPE" && (
              <div className="border rounded p-3 mb-3">
                <label className="form-label">Quotation Prototype</label>
                <select
                  className="form-select"
                  value={prototypeGuid}
                  onChange={(e) => applyPrototype(e.target.value)}
                  disabled={loadingPrototype}
                >
                  <option value="">Select a prototype…</option>
                  {prototypes.map((p) => (
                    <option key={p.recordGuid} value={p.recordGuid}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {prototypes.length === 0 && (
                  <small className="text-muted d-block mt-2">
                    No prototypes exist for this branch yet. They are set up under HR Masters &rarr; Quotation
                    Prototypes, and the same ones serve quoting.
                  </small>
                )}
                <small className="text-muted d-block mt-2">
                  Applying a prototype copies its lines. Editing the prototype later will not change equipment
                  already added.
                </small>
              </div>
            )}

            {/* ---- source 3: products ---- */}
            {source === "PRODUCTS" && (
              <div className="border rounded p-3 mb-3">
                <label className="form-label">Search products</label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions={false}
                  loadOptions={loadProductOptions}
                  onChange={addProduct}
                  value={null}
                  placeholder="Type at least 2 characters…"
                />
                <small className="text-muted d-block mt-2">
                  Only products marked as carrying a warranty appear here.
                </small>
              </div>
            )}

            {/* ---- skipped ---- */}
            {skipped.length > 0 && (
              <div className="alert alert-warning d-flex gap-2">
                <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
                <div>
                  <strong>{skipped.length} line(s) not added</strong>
                  <ul className="mb-0 ps-3">
                    {skipped.map((s, i) => (
                      <li key={i}>
                        {s.productName} — {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* ---- the editable grid, shared by all three sources ---- */}
            {rows.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 200 }}>Product</th>
                      <th style={{ width: 90 }}>Qty</th>
                      <th style={{ width: 150 }}>Serial no.</th>
                      <th style={{ width: 150 }}>Installed</th>
                      <th style={{ width: 90 }}>Warranty</th>
                      <th style={{ width: 150 }}>Provider</th>
                      <th style={{ width: 100 }}>Months</th>
                      <th style={{ width: 40 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.key}>
                        <td>
                          {r.productName}
                          {r.sourceStockOutLineId && <small className="d-block text-muted">from Stock Out</small>}
                          {r.sourcePrototypeId && <small className="d-block text-muted">from prototype</small>}
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className="form-control form-control-sm"
                            value={r.quantity}
                            onChange={(e) => setRow(r.key, { quantity: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={r.serialNumber}
                            placeholder="optional"
                            onChange={(e) => setRow(r.key, { serialNumber: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={r.installationDate}
                            onChange={(e) => setRow(r.key, { installationDate: e.target.value })}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={r.warrantyApplicable}
                            onChange={(e) => setRow(r.key, { warrantyApplicable: e.target.checked })}
                          />
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={r.warrantyProvider}
                            disabled={!r.warrantyApplicable}
                            onChange={(e) => setRow(r.key, { warrantyProvider: e.target.value })}
                          >
                            {WARRANTY_PROVIDERS.map((p) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="1"
                            className="form-control form-control-sm"
                            value={r.warrantyMonths}
                            disabled={!r.warrantyApplicable}
                            placeholder="default"
                            onChange={(e) => setRow(r.key, { warrantyMonths: e.target.value })}
                          />
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeRow(r.key)}
                            aria-label={`Remove ${r.productName}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <small className="text-muted">
                  Leave Months blank to use the product's own default warranty period. Warranty runs from the
                  installation date unless you set one explicitly.
                </small>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <span className="me-auto text-muted">
              {rows.length} item{rows.length === 1 ? "" : "s"} ready
            </span>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving || rows.length === 0}
            >
              {isSaving ? "Saving…" : "Add to register"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEquipmentModal;
